import { useMemo, useRef } from "react";
import { GridActionsCellItem, GridActionsColDef, GridColDef } from "@mui/x-data-grid";
import SysColumn from "app/class/sys-column";
import SysTable from "app/class/sys-table";
import { ColumnPreference } from "./useColumnPreferences";
import RecordListCellRendererComponent from "../components/RecordListCellRenderer";
import RecordListCellEditorComponent from "../components/RecordListCellEditor";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";
import { isRecordFieldReadOnly } from "modules/record/shared/services/recordFieldReadOnly";

const DEFAULT_COLUMN_WIDTH = 150;

const resolveCurrencyDependencyValue = (
    column: SysColumn,
    tableColumns: SysColumn[],
    row: Record<string, any>
) => {
    if (!column.depends_on_column_id) {
        return null;
    }

    const dependencyColumn = tableColumns.find((candidate) => candidate.id === column.depends_on_column_id);
    if (!dependencyColumn?.name) {
        return null;
    }

    const value = row?.[dependencyColumn.name];
    if (typeof value === "string") {
        return value;
    }

    if (value && typeof value === "object" && "id" in value) {
        return String((value as Record<string, unknown>).id ?? "");
    }

    return null;
};

interface UseRecordListColumnsParams {
    currentTable: SysTable;
    tableColumns: SysColumn[];
    preferences: ColumnPreference[];
    choiceLabelMaps: Record<string, Map<string, string>>;
    allColumns?: SysColumn[];
    tables?: SysTable[];
    actions?(params: any, table: SysTable): JSX.Element[];
    readOnly?: boolean;
    columnLabels?: Record<string, string>;
    isRelatedList: boolean;
    onNavigate: (id: string, event?: React.MouseEvent<HTMLElement>) => void;
    hiddenFieldNames?: Set<string>;
}

export function useRecordListColumns({
    currentTable,
    tableColumns,
    preferences,
    choiceLabelMaps,
    allColumns = [],
    tables = [],
    actions,
    readOnly = false,
    columnLabels,
    isRelatedList,
    onNavigate,
    hiddenFieldNames,
}: UseRecordListColumnsParams): (GridColDef | GridActionsColDef)[] {
    const { t, i18n } = useTranslation();

    const signature = useMemo(
        () =>
            JSON.stringify({
                table: currentTable.name,
                language: i18n.resolvedLanguage || i18n.language,
                columns: tableColumns.map((column) => ({
                    id: column.id,
                    name: column.name,
                    label: column.label,
                    translatedLabel: translateMetadataLabel(t, column),
                    type: column.column_type,
                    dependsOnColumnId: column.depends_on_column_id,
                    currencyCode: column.currency_code,
                    currencyDecimalPlaces: column.currency_decimal_places,
                    hidden: column.is_hidden,
                    readOnly: column.is_read_only,
                    order: column.order,
                })),
                preferences: preferences.map((preference) => ({
                    name: preference.name,
                    visible: preference.visible,
                    order: preference.order,
                    width: preference.width,
                })),
                choices: Object.entries(choiceLabelMaps).map(([columnId, valueMap]) => ({
                    columnId,
                    entries: Array.from(valueMap.entries()),
                })),
                conditionMetadata: tableColumns.some((column) => column.column_type === "CONDITION") ? {
                    columns: allColumns.map((column) => [column.id, column.sys_table_id, column.name, column.label, column.column_type]),
                    tables: tables.map((table) => [table.id, table.name]),
                } : null,
                hasActions: Boolean(actions),
                readOnly,
                columnLabels,
                hiddenFieldNames: hiddenFieldNames ? Array.from(hiddenFieldNames).sort() : [],
            }),
        [
            actions,
            readOnly,
            columnLabels,
            choiceLabelMaps,
            allColumns,
            tables,
            currentTable.name,
            hiddenFieldNames,
            i18n.language,
            i18n.resolvedLanguage,
            preferences,
            t,
            tableColumns,
        ]
    );

    const cacheRef = useRef<{
        signature: string;
        value: (GridColDef | GridActionsColDef)[];
    }>({
        signature: "",
        value: [],
    });

    return useMemo(() => {
        if (cacheRef.current.signature === signature) {
            return cacheRef.current.value;
        }

        const prefMap = new Map(preferences.map(p => [p.name, p]));

        const visibleColumns = tableColumns
            .filter(col => {
                if (hiddenFieldNames?.has(col.name)) {
                    return false;
                }

                const pref = prefMap.get(col.name);
                return pref ? pref.visible : !col.is_hidden;
            })
            .sort((a, b) => {
                const orderA = prefMap.get(a.name)?.order ?? a.order;
                const orderB = prefMap.get(b.name)?.order ?? b.order;
                return orderA - orderB;
            });

        const actionCol: GridActionsColDef = {
            field: "actions",
            type: "actions",
            width: 60,
            getActions: (params) =>
                actions
                    ? actions(params, currentTable)
                    : [
                        <GridActionsCellItem
                            icon={<InfoIcon />}
                            label={t("Information")}
                            onClick={(event) => onNavigate(params.row.id, event)}
                        />,
                    ],
        };

        const dataCols: GridColDef[] = visibleColumns.map(col => ({
            ...(Number.isFinite(prefMap.get(col.name)?.width) && (prefMap.get(col.name)?.width ?? 0) > 0
                ? { width: prefMap.get(col.name)?.width, flex: undefined }
                : { flex: 1, minWidth: DEFAULT_COLUMN_WIDTH }),
            field: col.name,
            headerName: columnLabels?.[col.name] ?? translateMetadataLabel(t, col),
            editable: !readOnly && !isRecordFieldReadOnly(col, currentTable.name) && !["CODE", "CONDITION", "RICH_HTML", "PASSWORD"].includes(col.column_type),
            renderCell: (params) => (
                <RecordListCellRendererComponent
                    column={col}
                    value={params.value}
                    row={params.row}
                    choiceLabelMap={choiceLabelMaps[col.id]}
                    currencyCodeOverride={resolveCurrencyDependencyValue(col, tableColumns, params.row)}
                    tableColumns={tableColumns}
                    choiceLabelMaps={choiceLabelMaps}
                    allColumns={allColumns}
                    tables={tables}
                />
            ),
            renderEditCell: (params) => (
                <RecordListCellEditorComponent column={col} params={params} />
            ),
        }));

        const nextValue = [actionCol, ...dataCols];

        cacheRef.current = {
            signature,
            value: nextValue,
        };

        return nextValue;
    }, [
        actions,
        readOnly,
        columnLabels,
        choiceLabelMaps,
        allColumns,
        tables,
        currentTable,
        hiddenFieldNames,
        isRelatedList,
        onNavigate,
        preferences,
        signature,
        tableColumns,
        t,
    ]);
}
